import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { TimeFormatter } from "@/hooks/useTimeFormatter";
import { Button } from "@/components/ui/button";
import { AlertCircle, Check, Trash2 } from "lucide-react";

export interface FeedbackMarkerData {
  id: string;
  anchor_time: string | null;
  author_name: string;
  body: string;
  status?: string;
}

interface ScheduleFeedbackMarkerProps {
  marker: FeedbackMarkerData;
  /** Pixel offset within the row's grid (= timeToPosition(anchor_time)). */
  left: number;
  /** Pulsing highlight when the producer locates this item from the list. */
  focused?: boolean;
  /** Stacking vs the host timeline (read-only uses z-40; the editable creator
   *  keeps its markers at z-20 to sit below its z-30 stage labels). */
  zClass?: string;
  /** Ring colour matching the row background (background vs card). */
  ringClass?: string;
  onResolve?: (id: string) => void;
  onDelete?: (id: string) => void;
  onEdit?: (marker: FeedbackMarkerData) => void;
  onOpenInCreator?: () => void;
  /**
   * Renders the anchor time. A prop, not a hook: the read-only timeline
   * resolves its formatter with the share payload's format override, and a
   * marker calling the hook itself cannot see that — it would show 24h inside
   * a 12h timeline on the public client page.
   */
  formatTime: TimeFormatter;
}

/**
 * One feedback dot + popover, shared by the read-only timeline and the editable
 * schedule creator so the marker looks and behaves identically on both. Open
 * feedback is a red "!", resolved is a green check. Available actions depend on
 * which handlers the host passes (producer: resolve/delete/open-in-creator;
 * client: edit/delete).
 */
export function ScheduleFeedbackMarker({
  marker,
  formatTime,
  left,
  focused,
  zClass = "z-40",
  ringClass = "ring-background",
  onResolve,
  onDelete,
  onEdit,
  onOpenInCreator,
}: ScheduleFeedbackMarkerProps) {
  const resolved = marker.status === "resolved";
  const hasActions = onResolve || onDelete || onEdit || onOpenInCreator;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-feedback-id={marker.id}
          className={`absolute ${zClass} flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full shadow-md ring-2 ${ringClass} transition-transform hover:scale-110 ${
            resolved ? "bg-emerald-500 text-white" : "bg-destructive text-destructive-foreground"
          } ${focused ? "scale-125 animate-pulse ring-4 ring-primary" : ""}`}
          style={{ left: `${left}px`, top: 6 }}
          title={`Feedback from ${marker.author_name}`}
          onClick={(e) => e.stopPropagation()}
        >
          {resolved ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">{marker.author_name}</span>
            <span className="text-xs text-muted-foreground">{formatTime(marker.anchor_time)}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm">{marker.body}</p>
          {resolved && (
            <p className="text-xs font-medium text-emerald-600">Resolved by the producer</p>
          )}
          {hasActions && (
            <div className="flex flex-wrap justify-end gap-1 pt-1">
              {onOpenInCreator && (
                <Button variant="ghost" size="sm" onClick={onOpenInCreator}>
                  Open in creator
                </Button>
              )}
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={() => onEdit(marker)}>
                  Edit
                </Button>
              )}
              {onResolve && !resolved && (
                <Button variant="ghost" size="sm" onClick={() => onResolve(marker.id)}>
                  <Check className="mr-1 h-4 w-4" />
                  Resolve
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onDelete(marker.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
